import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ThemeToggle from './ThemeToggle';
import { API_CONFIG, APP_CONFIG } from '../contexts/constants';
import { showAlert } from '../utils/sweetAlertConfig';

// Continuous float-up balls (no fixed count): slowly spawn and remove when off-screen
// Notes:
// - Each ball animates from below viewport to above, then is removed.
// - Spawning continues forever to create a never-ending flow.
const useFloatingBalls = () => {
	const [balls, setBalls] = useState([]);
	const nextId = useRef(0);
	const timers = useRef([]);

	useEffect(() => {
		let isMounted = true;
		const spawnIntervalMs = 2200; // slower cadence
		const redMinSepPct = 18; // reds should be far apart horizontally
		const FORBID_LEFT = 30; // avoid central form area
		const FORBID_RIGHT = 70;

		const scheduleRemoval = (id, totalMs) => {
			const t = setTimeout(() => {
				if (!isMounted) return;
				setBalls(prev => prev.filter(b => b.id !== id));
			}, totalMs + 80);
			timers.current.push(t);
		};

		const spawn = () => {
			setBalls(prev => {
				// Decide color (no hard cap), weight gray higher
				const wantRed = Math.random() < 0.22;
				let color = wantRed ? 'red' : 'gray';
			const num = color === 'gray' ? (Math.floor(Math.random() * 69) + 1) : (Math.floor(Math.random() * 26) + 1);

			// Size smaller to keep subtle background when many are on screen
			const size = Math.floor(Math.random() * 19) + 36; // 36..54 px

			// Horizontal position; avoid center form zone and enforce extra spacing for red-vs-red
			let left = Math.random() * 88 + 6; // 6%..94%
			let attempts = 0;
			const checkAndUpdate = () => {
				const inForbidden = left >= FORBID_LEFT && left <= FORBID_RIGHT;
				const tooCloseRed = color === 'red' && prev.some(b => b.color === 'red' && Math.abs(b.left - left) < redMinSepPct);
				return !inForbidden && !tooCloseRed;
			};
			while (attempts++ < 20) {
				if (checkAndUpdate()) break;
				left = Math.random() * 88 + 6;
			}				// Timing: slower float up
				const delay = +(Math.random() * 1.2).toFixed(2); // 0..1.2s
				const dur = +(28 + Math.random() * 14).toFixed(2); // 28..42s

				const id = nextId.current++;
				// Schedule removal after one run
				scheduleRemoval(id, (delay + dur) * 1000);

				return [...prev, { id, color, num, left, size, delay, dur }];
			});
		};

		// Kick off a few initial spawns quickly for a filled scene
		spawn();
		setTimeout(spawn, 300);
		setTimeout(spawn, 700);

		const intv = setInterval(spawn, spawnIntervalMs);
		return () => {
			isMounted = false;
			clearInterval(intv);
			timers.current.forEach(clearTimeout);
			timers.current = [];
		};
	}, []);

		const setBallNum = (id, num) => {
			setBalls(prev => prev.map(b => (b.id === id ? { ...b, num } : b)));
		};

		return { balls, setBallNum };
};

const Login = ({ onLogin }) => {
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [captchaImg, setCaptchaImg] = useState('');
	const [captchaId, setCaptchaId] = useState('');
	const [captchaInput, setCaptchaInput] = useState('');
	const [captchaLoading, setCaptchaLoading] = useState(false);
	// no longer using math question
	const [lastRefresh, setLastRefresh] = useState(0);
	const [refreshError, setRefreshError] = useState('');
	const [systemOpen, setSystemOpen] = useState(1);

	const fetchCaptcha = async () => {
		const now = Date.now();
		// throttle: 1 request per 1.2s
		if (now - lastRefresh < 1200) {
			setRefreshError('รีเฟรชบ่อยเกินไป รอสักครู่');
			return;
		}
		setRefreshError('');
		setLastRefresh(now);
		setCaptchaLoading(true);
		try {
			const { data } = await axios.get(`${API_CONFIG.BASE_URL}/captcha`);
			setCaptchaImg(data.data);
			setCaptchaId(data.id);
			setCaptchaInput('');
		} catch (e) {
			console.error('Fetch captcha error', e);
			setCaptchaImg('');
		} finally {
			setCaptchaLoading(false);
		}
	};

	const fetchConfigLogin = async () => {
		try {
			const { data } = await axios.get(`${API_CONFIG.BASE_URL}/config/login`, {
				headers: {
					'Content-Type': 'application/json'
				}
			});

			if (data.success) {
				setSystemOpen(data.data.systemOpen || '');

				const timer = setTimeout(showVideoPopup(data.data.youtubeVideo || ''), 500);
				clearTimeout(timer);
			}
		} catch (error) {
			console.error('Error fetching Config Login:', error);
		}
	};

	const showVideoPopup = (youtubeVideo) => {
		if (!youtubeVideo) return;

		const isMobile = window.innerWidth <= 768;
		const videoHeight = isMobile ? Math.min(window.innerHeight * 0.5, 350) : 550;
		const logoSize = isMobile ? 48 : 64;
		const titleSize = isMobile ? '1.25rem' : '1.75rem';
		const popupPadding = isMobile ? '1rem' : '2em';
		const popupWidth = isMobile ? '95vw' : '80vw';

		showAlert({
			html: `
				<div style="text-align: center;margin-bottom: ${isMobile ? '1rem' : '1.5rem'};display: flex;align-items: center;justify-content: center;flex-wrap: wrap;">
					<img src="${APP_CONFIG.LOGO_PATH}" alt="Logo" style="width: ${logoSize}px; height: ${logoSize}px;margin-right: ${isMobile ? '0.5rem' : '1rem'};" />
					<h2 style="font-size: ${titleSize}; font-weight: 700; margin: 0;">${APP_CONFIG.APP_NAME}</h2>
				</div>
				<iframe width="100%" height="${videoHeight}" src="https://www.youtube.com/embed/${youtubeVideo}?autoplay=1&mute=1" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="border-radius: 8px;"></iframe>
			`,
			showCloseButton: true,
			showConfirmButton: false,
			width: popupWidth,
			padding: popupPadding,
			backdrop: true,
			customClass: {
				popup: 'video-popup-border'
			}
		});
	};

	useEffect(() => {
		fetchCaptcha();
		fetchConfigLogin();
		document.title = `เข้าสู่ระบบ | ${APP_CONFIG.APP_NAME}`;
	}, []);

		// Endless, slow floating balls
		const { balls, setBallNum } = useFloatingBalls();
		// Ensure pause on hover via inline style (in addition to CSS), for reliability
		const [hoveredIds, setHoveredIds] = useState(() => new Set());
				const onEnter = (id) => setHoveredIds(prev => { const s = new Set(prev); s.add(id); return s; });
				const onLeave = (id) => setHoveredIds(prev => { const s = new Set(prev); s.delete(id); return s; });
				const onClickBall = (ball) => {
					const max = ball.color === 'red' ? 26 : 69;
					const newNum = Math.floor(Math.random() * max) + 1;
					setBallNum(ball.id, newNum);
				};

  const handleSubmit = async (e) => {
    e.preventDefault();
		if (!captchaId || !captchaInput) {
			setError('กรุณากรอก CAPTCHA');
			return;
		}
		setError('');
		setLoading(true);

    try {
      // เรียก API สำหรับ login
			const response = await axios.post(`${API_CONFIG.BASE_URL}/login`, {
				username,
				password,
				captchaId,
				captchaAnswer: captchaInput
			});

	if (response.data.success) {
        // เก็บ token ใน localStorage
        localStorage.setItem('userID', response.data.user.id);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('username', username);
        localStorage.setItem('name', response.data.user.name);
        // เก็บ userRole (ถ้า API ส่งมา หรือใช้ค่า default)
        const role = response.data.user.role || 'customer';
        localStorage.setItem('userRole', role);
        // เก็บ tier สำหรับ customer
        if (response.data.user.tier) {
          localStorage.setItem('tier', response.data.user.tier);
        }
        onLogin(role);
      } else {
				setError(response.data.message || 'Login failed');
				fetchCaptcha();
      }
		} catch (error) {
			console.error('Login error:', error);
			setError(error?.response?.data?.message || 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ หรือข้อมูลไม่ถูกต้อง');
			fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

return (
	<div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden bg-gradient-to-b from-blue-50 via-blue-50 to-blue-100 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900">
    	{/* Lotto balls background (endless slow float-up) */}
		<div className="lotto-balls">
			{balls.map(b => (
				<div
					key={b.id}
					className={`lotto-ball ${b.color}`}
					style={{
						left: `${b.left}%`,
						top: `0%`,
						width: `${b.size}px`,
						height: `${b.size}px`,
						fontSize: `${Math.max(12, Math.floor(b.size * 0.36))}px`,
						animation: `floatUp ${b.dur}s linear ${b.delay}s 1 both`,
            animationPlayState: hoveredIds.has(b.id) ? 'paused' : 'running',
						cursor: 'pointer',
						pointerEvents: 'auto',
					}}
					onMouseEnter={() => onEnter(b.id)}
					onMouseLeave={() => onLeave(b.id)}
					onClick={() => onClickBall(b)}
					title="คลิกเพื่อสุ่มเลข"
					role="button"
					tabIndex={0}
					onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClickBall(b); } }}
				>
					{b.num}
				</div>
			))}
		</div>
		<div className="absolute top-5 right-5">
			<ThemeToggle />
		</div>

		<form
			onSubmit={handleSubmit}
			className="relative z-10 w-full max-w-md rounded-xl shadow-2xl p-10 transition-colors duration-300 backdrop-blur-md bg-white/85 dark:bg-gray-900/85 border border-blue-200/70 dark:border-gray-700/50"
		>


			<div className="flex items-center justify-center gap-3 mb-8">
				<img
					src={APP_CONFIG.LOGO_PATH}
					alt="Logo"
					className="w-12 h-12"
					loading="lazy"
					decoding="async"
				/>
				<h2 className="text-3xl font-semibold text-gray-900 dark:text-white">{APP_CONFIG.APP_NAME}</h2>
			</div>

			<div className="mb-5">
				<label htmlFor="username" className="block mb-2 text-gray-700 dark:text-gray-200 font-medium">ชื่อผู้ใช้:</label>
				<input
					type="text"
					id="username"
					value={username}
					onChange={(e) => setUsername(e.target.value)}
					required
					placeholder="กรุณากรอกชื่อผู้ใช้"
					className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition"
				/>
			</div>

			<div className="mb-5">
				<label htmlFor="password" className="block mb-2 text-gray-700 dark:text-gray-200 font-medium">รหัสผ่าน:</label>
				<div className="relative flex items-center">
					<input
						type={showPassword ? 'text' : 'password'}
						id="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						required
						placeholder="กรุณากรอกรหัสผ่าน"
						className="w-full pr-11 px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition"
					/>
					<button
						type="button"
						className="absolute right-2 text-gray-600 hover:text-blue-600 transition p-1 dark:text-gray-300 dark:hover:text-blue-400"
						aria-label={showPassword ? 'Hide password' : 'Show password'}
						onClick={() => setShowPassword(prev => !prev)}
					>
						{showPassword ? (
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
								<path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.83 21.83 0 0 1-2.16 3.19M14.12 14.12A3 3 0 0 1 9.88 9.88M1 1l22 22" />
							</svg>
						) : (
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
								<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
								<circle cx="12" cy="12" r="3" />
							</svg>
						)}
					</button>
				</div>
			</div>

			<div className="mb-5">
				<div className="flex items-center justify-center gap-3 mb-3">
					<label className="block mb-2 text-gray-700 dark:text-gray-200 font-medium">CAPTCHA:</label>
					{captchaImg ? (
						<div className="h-14 flex items-center justify-center rounded border-2 border-blue-200 dark:border-gray-600 bg-white  select-none">
							<img src={captchaImg} alt="captcha" className="h-full w-auto"/>
						</div>
					) : (
						<div className="h-14 min-w-[110px] flex items-center justify-center px-4 rounded border-2 border-blue-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
							{captchaLoading ? 'กำลังโหลด...' : 'โหลดไม่ได้'}
						</div>
					)}
					<button
						type="button"
						onClick={fetchCaptcha}
						disabled={captchaLoading}
						className="px-3 py-2 text-xs rounded bg-blue-100 dark:bg-gray-700 text-blue-700 dark:text-gray-200 hover:bg-blue-200 dark:hover:bg-gray-600 transition disabled:opacity-50 flex items-center justify-center"
						aria-label="Refresh CAPTCHA"
					>
						{/* Refresh Icon */}
						<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582M20 20v-5h-.581M19.418 9A7.978 7.978 0 0 0 12 4c-4.418 0-8 3.582-8 8m16 0c0 1.657-.672 3.157-1.762 4.243M4.582 15A7.978 7.978 0 0 0 12 20c4.418 0 8-3.582 8-8" />
						</svg>
					</button>
				</div>
				{refreshError && <div className="mb-2 text-xs text-red-600 dark:text-red-400">{refreshError}</div>}
				<input
					type="text"
					value={captchaInput}
					onChange={(e) => setCaptchaInput(e.target.value)}
					placeholder="พิมพ์ตัวอักษรที่เห็น"
					className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition"
					required
				/>
			</div>

			{systemOpen !== 1 && (
			<div className="my-5 text-center text-md text-red-600 dark:text-red-300">
				<p>ระบบปิดการใช้งานอยู่</p>
			</div>)}

			<button
				type="submit"
				disabled={loading}
				className="w-full py-3 text-white font-semibold rounded-lg shadow-lg transition disabled:opacity-60 disabled:cursor-not-allowed hover:-translate-y-0.5 transform bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 dark:from-blue-700 dark:to-blue-900 dark:hover:from-blue-800 dark:hover:to-blue-950"
			>
				{loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
			</button>

			{error && <div className="mt-4 text-center text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded p-2">{error}</div>}

			<div className="mt-5 text-center text-sm text-gray-600 dark:text-gray-300">
				<p>ยินดีต้อนรับลูกค้าทุกท่าน</p>
			</div>
		</form>
	</div>
);
};

export default Login;
